/**
 * Collapsible Practice (exercises) and Quiz sections at the end of a lesson.
 */

import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronDown, ClipboardList, PenLine } from 'lucide-react';
import { CollapsibleFadePanel } from '../shared/CollapsibleFadePanel';
import type { LessonExerciseSummaryDto, LessonQuizDto } from '../../services/contentService';
import { countAnswerableQuizQuestions } from '../../utils/quizQuestionCount';
import { LearnerQuizCard } from './LearnerQuizCard';
import { SubscriptionPlanIcons } from '../shared/SubscriptionPlanIcons';
import { ClickableTooltip } from '../shared/ClickableTooltip';
import type { SubscriptionPlan } from '../../types/subscription';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useLocalizedContent } from '../../hooks/useLocalizedContent';
import { PersianText } from '../shared/PersianText';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';

function ExpandableSection({
  panelId,
  expanded,
  onToggle,
  header,
  children,
  borderClass,
  bgClass
}: {
  panelId: string;
  expanded: boolean;
  onToggle: () => void;
  header: React.ReactNode;
  children: React.ReactNode;
  borderClass: string;
  bgClass: string;
}) {
  const { direction } = useAppLocale();

  return (
    <div
      className={`overflow-hidden rounded-2xl border shadow-sm ${borderClass} ${bgClass}`}
      dir={direction}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-3 py-3 text-start transition-colors hover:opacity-95 sm:px-6"
        aria-expanded={expanded}
        aria-controls={panelId}
      >
        {header}
        <ChevronDown
          className={`h-5 w-5 shrink-0 transition-transform duration-200 ease-out ${
            expanded ? 'rotate-180' : 'rotate-0'
          }`}
          aria-hidden
        />
      </button>
      <CollapsibleFadePanel expanded={expanded} panelId={panelId}>
        <div className="border-t border-inherit px-3 pb-3 pt-3 sm:px-6 sm:pb-6">{children}</div>
      </CollapsibleFadePanel>
    </div>
  );
}

function PracticeExerciseCard({
  ex,
  completed,
  subscriptionPlans,
  isRtl,
  direction,
  pickItemTitle,
  pickItemDescription,
  onStartExercise,
}: {
  ex: LessonExerciseSummaryDto;
  completed: boolean;
  subscriptionPlans: SubscriptionPlan[];
  isRtl: boolean;
  direction: 'ltr' | 'rtl';
  pickItemTitle: (item: { title?: string; titleFa?: string; isDesignedForNonFarsiUsers?: boolean }) => string;
  pickItemDescription: (item: {
    description?: string;
    descriptionFa?: string;
    isDesignedForNonFarsiUsers?: boolean;
  }) => string;
  onStartExercise: LessonPracticeQuizExpandablesProps['onStartExercise'];
}) {
  const { t } = useTranslation('learning');
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const cardRtl = isRtl;

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border border-amber-200/85 bg-white/90 p-4 dark:border-amber-800/45 dark:bg-gray-800/60 sm:flex-row sm:items-center sm:justify-between ${
        completed ? 'border-b-[3px] border-b-emerald-500 dark:border-b-emerald-400' : ''
      }`}
      dir={direction}
    >
      <div
        className={`flex min-w-0 flex-1 items-start gap-2 sm:items-center ${cardRtl ? 'flex-row-reverse' : ''}`}
        dir="ltr"
      >
        <div className="flex shrink-0 items-center gap-1.5">
          <ClickableTooltip tooltip={t('exercise.tooltipLabel')}>
            <div className="rounded-xl bg-amber-100/95 p-2 text-amber-800 dark:bg-amber-900/35 dark:text-amber-200">
              <PenLine className="h-5 w-5" />
            </div>
          </ClickableTooltip>
          <SubscriptionPlanIcons planIds={ex.subscriptionPlanIds ?? []} plans={subscriptionPlans} compact />
        </div>
        <div className="min-w-0 flex-1 text-start" dir={direction}>
          <h3
            className={`flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-100 ${
              cardRtl ? 'flex-row-reverse justify-end' : ''
            }`}
          >
            {completed && (
              <CheckCircle2
                className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                aria-label={t('exercise.passedAria')}
              />
            )}
            <span>
              <PersianText>{pickItemTitle(ex)}</PersianText>
            </span>
          </h3>
          {!simpleHome && pickItemDescription(ex) ? (
            <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-gray-600 dark:text-gray-400">
              <PersianText>{pickItemDescription(ex)}</PersianText>
            </p>
          ) : null}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            {t(
              (ex.questionCount ?? 0) === 1 ? 'exercise.questionCount_one' : 'exercise.questionCount_other',
              { count: ex.questionCount ?? 0 }
            )}
            {ex.mandatoryForPassingLesson ? t('assessment.requiredToPassSuffix') : ''}
          </p>
        </div>
      </div>
      <div className={`flex shrink-0 flex-col items-stretch gap-1.5 ${cardRtl ? 'sm:items-start' : 'sm:items-end'}`}>
        <button
          type="button"
          onClick={(e) =>
            onStartExercise(
              {
                id: ex.id,
                title: pickItemTitle(ex),
                subscriptionPlanIds: ex.subscriptionPlanIds ?? [],
              },
              e
            )
          }
          className="rounded-lg bg-gradient-to-r from-amber-600 to-amber-700 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:from-amber-500 hover:to-amber-600"
        >
          {completed ? t('exercise.practiceAgain') : t('exercise.startExercise')}
        </button>
        {completed && (
          <p
            className={`text-center text-xs font-medium text-emerald-700 dark:text-emerald-400 ${cardRtl ? 'sm:text-start' : 'sm:text-end'}`}
          >
            {t('exercise.passedThisPractice')}
          </p>
        )}
      </div>
    </div>
  );
}

export interface LessonPracticeQuizExpandablesProps {
  exercises: LessonExerciseSummaryDto[];
  quizzes: LessonQuizDto[];
  subscriptionPlans: SubscriptionPlan[];
  onStartExercise: (
    ex: { id: number; title: string; subscriptionPlanIds?: number[] },
    event?: React.MouseEvent<HTMLElement>
  ) => void;
  onStartQuiz: (quizId: number, event?: React.MouseEvent<HTMLElement>) => void;
  className?: string;
}

export const LessonPracticeQuizExpandables: React.FC<LessonPracticeQuizExpandablesProps> = ({
  exercises,
  quizzes,
  subscriptionPlans,
  onStartExercise,
  onStartQuiz,
  className = ''
}) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const { pickTitle: pickItemTitle, pickDescription: pickItemDescription } = useLocalizedContent();
  const isRtl = direction === 'rtl';
  const [exercisesOpen, setExercisesOpen] = useState(false);
  const [quizzesOpen, setQuizzesOpen] = useState(false);
  const exercisePanelId = useId();
  const quizPanelId = useId();

  if (exercises.length === 0 && quizzes.length === 0) return null;

  return (
    <div className={`space-y-4 ${className}`}>
      {exercises.length > 0 && (
        <ExpandableSection
          panelId={exercisePanelId}
          expanded={exercisesOpen}
          onToggle={() => setExercisesOpen((o) => !o)}
          borderClass="border-amber-200/85 dark:border-amber-900/35"
          bgClass="bg-gradient-to-br from-white via-amber-50/70 to-orange-50/35 dark:from-slate-900 dark:via-amber-950/25 dark:to-slate-950"
          header={
            <div
              className={`flex min-w-0 flex-1 items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}
              dir="ltr"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-md">
                <PenLine className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1 text-start" dir={direction}>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {t('lessonPractice.title')}
                </span>
                {simpleHome ? null : (
                <p className="text-xs text-amber-900/80 dark:text-amber-200/90">
                  {t('lessonPractice.exercisesSubtitle')}
                </p>
                )}
              </div>
              <span className="shrink-0 rounded-full bg-amber-100/95 px-2.5 py-0.5 text-xs font-bold text-amber-900 dark:bg-amber-900/45 dark:text-amber-50/95">
                {exercises.length}
              </span>
            </div>
          }
        >
          <div className="grid gap-3">
            {exercises.map((ex) => (
              <PracticeExerciseCard
                key={ex.id}
                ex={ex}
                completed={ex.userHasCompletedExercise === true}
                subscriptionPlans={subscriptionPlans}
                isRtl={isRtl}
                direction={direction}
                pickItemTitle={pickItemTitle}
                pickItemDescription={pickItemDescription}
                onStartExercise={onStartExercise}
              />
            ))}
          </div>
        </ExpandableSection>
      )}

      {quizzes.length > 0 && (
        <ExpandableSection
          panelId={quizPanelId}
          expanded={quizzesOpen}
          onToggle={() => setQuizzesOpen((o) => !o)}
          borderClass="border-purple-200/90 dark:border-purple-900/45"
          bgClass="bg-gradient-to-br from-white via-violet-50/50 to-purple-50/40 dark:from-slate-900 dark:via-violet-950/25 dark:to-slate-950"
          header={
            <div
              className={`flex min-w-0 flex-1 items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}
              dir="ltr"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-md">
                <ClipboardList className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1 text-start" dir={direction}>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {t('lessonPractice.lessonQuizTitle')}
                </span>
                {simpleHome ? null : (
                <p className="text-xs text-violet-700/90 dark:text-violet-200/80">
                  {quizzes.length === 1
                    ? t('lessonPractice.lessonQuizSubtitleSingle')
                    : t('lessonPractice.lessonQuizCount', { count: quizzes.length })}
                </p>
                )}
              </div>
              <span className="shrink-0 rounded-full bg-violet-100/95 px-2.5 py-0.5 text-xs font-bold text-violet-900 dark:bg-violet-900/45 dark:text-violet-50/95">
                {quizzes.length}
              </span>
            </div>
          }
        >
          <div className="space-y-3">
            {quizzes.map((qz) => (
              <LearnerQuizCard
                key={qz.id}
                compactDescription
                title={pickItemTitle(qz)}
                {...(!simpleHome && pickItemDescription(qz) ? { description: pickItemDescription(qz) } : {})}
                questionCount={countAnswerableQuizQuestions(qz.questions)}
                timeLimitMinutes={qz.timeLimitMinutes}
                passingScore={qz.passingScore}
                mandatoryForPassing={Boolean(qz.mandatoryForPassingLesson)}
                {...(qz.userHasPassedQuiz !== undefined ? { userHasPassedQuiz: qz.userHasPassedQuiz } : {})}
                {...(qz.userBestQuizScore !== undefined ? { userBestQuizScore: qz.userBestQuizScore } : {})}
                onAction={(e) => onStartQuiz(qz.id, e)}
              />
            ))}
          </div>
        </ExpandableSection>
      )}
    </div>
  );
};
